import React, { useEffect, useState } from "react";
import { List, ChevronDown, GripVertical, RotateCcw } from "lucide-react";
import { Column } from "../../types";
import { useAppDispatch } from "../../hooks/useStore";
import {
  updateShipmentColumns,
  resetShipmentColumns,
} from "../../store/slices/shipmentSlice";
import { toast } from "react-toastify";
import Button from "../UI/Button";
import { useTranslation } from "react-i18next";
import { columnDisplayName } from "../../utils/shipmentColumns";

interface ColumnConfigDropdownProps {
  columns: Column[];
  setColumns: (columns: Column[]) => void;
  isColumnConfigOpen: boolean;
  setIsColumnConfigOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const ColumnConfigDropdown: React.FC<ColumnConfigDropdownProps> = ({
  columns,
  setColumns,
  isColumnConfigOpen,
  setIsColumnConfigOpen,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation("shipments");
  const [localColumns, setLocalColumns] = useState<Column[]>(columns);
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  useEffect(() => {
    setLocalColumns(columns);
    setHasChanges(false);
  }, [columns]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest(".column-config-dropdown")) {
        if (hasChanges) {
          handleSave();
        }
        setIsColumnConfigOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [setIsColumnConfigOpen, hasChanges, localColumns]);

  const handleDragStart = (columnId: string) => {
    setDraggedColumn(columnId);
  };

  const handleDragOver = (e: React.DragEvent, columnId: string) => {
    e.preventDefault();
    setDragOverColumn(columnId);
  };

  const handleDragLeave = () => {
    setDragOverColumn(null);
  };

  const handleDrop = (e: React.DragEvent, targetColumnId: string) => {
    e.preventDefault();

    if (draggedColumn && draggedColumn !== targetColumnId) {
      const newColumns = [...localColumns];
      const draggedIndex = newColumns.findIndex(
        (col) => col.id === draggedColumn,
      );
      const targetIndex = newColumns.findIndex(
        (col) => col.id === targetColumnId,
      );

      if (draggedIndex !== -1 && targetIndex !== -1) {
        const [movedColumn] = newColumns.splice(draggedIndex, 1);
        newColumns.splice(targetIndex, 0, movedColumn);
        setLocalColumns(newColumns);
        setHasChanges(true);
      }
    }

    setDraggedColumn(null);
    setDragOverColumn(null);
  };

  const toggleColumnVisibility = (columnId: string) => {
    setLocalColumns((prevColumns) =>
      prevColumns.map((col) =>
        col.id === columnId ? { ...col, visible: !col.visible } : col,
      ),
    );
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!hasChanges) return;

    try {
      await dispatch(updateShipmentColumns(localColumns)).unwrap();
      setColumns(localColumns);
      setHasChanges(false);
      toast.success(t("columns.savedSuccess"));
    } catch (error) {}
  };

  useEffect(() => {
    if (hasChanges) {
      const timer = setTimeout(() => {
        handleSave();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [localColumns, hasChanges]);

  const handleReset = async () => {
    const confirmReset = window.confirm(t("columns.confirmReset"));
    if (!confirmReset) return;

    setIsResetting(true);
    try {
      const resetColumns = await dispatch(resetShipmentColumns()).unwrap();
      setLocalColumns(resetColumns);
      setColumns(resetColumns);
      setHasChanges(false);
      toast.success(t("columns.resetSuccess"));
    } catch (error) {
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsColumnConfigOpen(!isColumnConfigOpen)}
        className="inline-flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
      >
        <List className="h-4 w-4" />
        <span>{t("columns.columns")}</span>
        <ChevronDown className="h-4 w-4" />
      </button>

      {isColumnConfigOpen && (
        <div className="column-config-dropdown absolute max-md:w-[287px] sm:right-0 mt-2 w-96 max-w-[calc(100vw-2rem)] max-h-[min(70vh,32rem)] bg-white rounded-lg shadow-lg border border-dark-200 z-20 flex flex-col">
          {/* Pinned header */}
          <div className="shrink-0 px-4 pt-4 pb-2">
            <h3 className="text-sm font-medium text-gray-700">
              {t("columns.configureColumns")}
            </h3>
          </div>

          {/* Scrollable, draggable list */}
          <div className="flex-1 min-h-0 overflow-y-auto dropdown-scroll px-4">
            <div className="space-y-2 py-1">
              {localColumns.map((column) => (
                <div
                  key={column.id}
                  draggable
                  onDragStart={() => handleDragStart(column.id)}
                  onDragEnd={handleDragLeave}
                  onDragOver={(e) => handleDragOver(e, column.id)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, column.id)}
                  className={`flex items-center justify-between py-1 px-2 rounded transition-all cursor-move ${
                    dragOverColumn === column.id
                      ? "bg-blue-50 ring-1 ring-blue-300"
                      : ""
                  } ${draggedColumn === column.id ? "opacity-50" : ""}`}
                >
                  <div className="flex items-center space-x-2 min-w-0">
                    <GripVertical className="h-4 w-4 text-gray-400 shrink-0" />
                    <span className="text-sm text-gray-600 truncate">
                      {column.name === "Direction"
                        ? t("table.inOut")
                        : columnDisplayName(column)}
                    </span>
                  </div>
                  {column.fixed ? (
                    <div className="w-10 h-5 bg-gray-100 rounded-full relative shrink-0">
                      <div className="absolute left-0 top-0 w-5 h-5 bg-gray-300 rounded-full"></div>
                    </div>
                  ) : (
                    <button
                      onClick={() => toggleColumnVisibility(column.id)}
                      onPointerDown={(e) => e.stopPropagation()}
                      draggable={false}
                      className={`w-10 h-5 rounded-full relative transition-colors duration-200 shrink-0 cursor-pointer ${
                        column.visible ? "bg-green-500" : "bg-gray-300"
                      }`}
                    >
                      <div
                        className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform duration-200 ${
                          column.visible ? "translate-x-5" : "translate-x-0.5"
                        }`}
                      ></div>
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Pinned footer — reset always visible */}
          <div className="shrink-0 p-4 border-t border-gray-200 rounded-b-lg">
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="primary"
                size="sm"
                onClick={handleReset}
                icon={<RotateCcw className="h-5 w-5" />}
                className="p-2!"
                isLoading={isResetting}
              >
                <span>
                  {isResetting ? t("columns.resetting") : t("columns.reset")}
                </span>
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ColumnConfigDropdown;
